import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function PrivacyPolicy() {
  const [lastUpdated] = useState("January 21, 2025");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="bg-white/90 backdrop-blur shadow-xl border-purple-200">
          <div className="p-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-600 mb-8">Last updated: {lastUpdated}</p>

            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-8 text-gray-800">
                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">1. Introduction</h2>
                  <p className="leading-relaxed">
                    SnuggleCrafters.com ("we," "our," or "us"), operated by VibeCaaS.com, is committed to protecting your privacy. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use 
                    our AI-powered bedtime story generation service.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">2. Information We Collect</h2>
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-teal-700">Personal Information</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Name and email address (for account creation)</li>
                      <li>Password (encrypted and securely stored)</li>
                      <li>Billing information (processed securely through Stripe)</li>
                      <li>Child's name, age, and interests (for story personalization)</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-teal-700 mt-4">Usage Information</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Stories generated and saved</li>
                      <li>Reading preferences and favorites</li>
                      <li>Audio playback settings</li>
                      <li>Device and browser information</li>
                      <li>IP address and approximate location</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-teal-700 mt-4">Cookies and Tracking</h3>
                    <p className="leading-relaxed">
                      We use essential cookies for authentication and user preferences. We do not use tracking 
                      cookies for advertising purposes.
                    </p>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">3. How We Use Your Information</h2>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>To provide and personalize the story generation service</li>
                    <li>To process payments and manage subscriptions</li>
                    <li>To save and retrieve your stories</li>
                    <li>To improve our AI models and service quality</li>
                    <li>To send service-related communications</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">4. Data Sharing and Disclosure</h2>
                  <div className="space-y-4">
                    <p className="leading-relaxed">We do not sell your personal information. We may share your data with:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Service Providers:</strong> OpenAI (for story generation), Stripe (for payments), 
                          Lulu (for book printing)</li>
                      <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                      <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
                    </ul>
                    <p className="leading-relaxed mt-4">
                      All third-party providers are contractually obligated to protect your information and use it 
                      only for providing their services.
                    </p>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">5. Children's Privacy</h2>
                  <p className="leading-relaxed">
                    SnuggleCrafters.com is designed for parents to create stories for children. We do not knowingly 
                    collect personal information directly from children under 13. Parents maintain full control 
                    over the information used to personalize stories and can delete it at any time.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">6. Data Security</h2>
                  <div className="space-y-4">
                    <p className="leading-relaxed">We implement industry-standard security measures including:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Encryption of data in transit and at rest</li>
                      <li>Secure password hashing</li>
                      <li>Regular security audits</li>
                      <li>Limited access to personal information</li>
                      <li>Secure payment processing through Stripe</li>
                    </ul>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">7. Your Rights (GDPR/CCPA)</h2>
                  <div className="space-y-4">
                    <p className="leading-relaxed">You have the right to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Access:</strong> Request a copy of your personal data</li>
                      <li><strong>Rectification:</strong> Correct inaccurate information</li>
                      <li><strong>Erasure:</strong> Delete your account and associated data</li>
                      <li><strong>Portability:</strong> Export your stories and data</li>
                      <li><strong>Object:</strong> Opt-out of certain data processing</li>
                      <li><strong>Restrict:</strong> Limit how we use your data</li>
                    </ul>
                    <p className="leading-relaxed mt-4">
                      To exercise these rights, contact us at privacy@VibeCaaS.com
                    </p>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">8. Data Retention</h2>
                  <p className="leading-relaxed">
                    We retain your personal information for as long as your account is active. Stories and associated 
                    data are kept until you delete them. After account deletion, we may retain certain information 
                    for legal compliance and fraud prevention for up to 90 days.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">9. International Data Transfers</h2>
                  <p className="leading-relaxed">
                    Your information may be transferred to and processed in countries other than your country of 
                    residence. We ensure appropriate safeguards are in place to protect your information in 
                    accordance with this Privacy Policy.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">10. Changes to Privacy Policy</h2>
                  <p className="leading-relaxed">
                    We may update this Privacy Policy from time to time. We will notify you of significant changes 
                    via email or through the Service. Your continued use after changes constitutes acceptance of 
                    the updated policy.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">11. Contact Information</h2>
                  <div className="space-y-4">
                    <p className="leading-relaxed">
                      For privacy-related questions or to exercise your rights, contact us at:
                    </p>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="font-semibold">VibeCaaS.com Privacy Team</p>
                      <p>Email: privacy@VibeCaaS.com</p>
                      <p>Data Protection Officer: dpo@VibeCaaS.com</p>
                      <p>Address: San Francisco, CA, USA</p>
                    </div>
                    <p className="leading-relaxed mt-4">
                      For EU residents: You have the right to lodge a complaint with your local supervisory authority.
                    </p>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">12. California Privacy Rights</h2>
                  <p className="leading-relaxed">
                    California residents have additional rights under the CCPA, including the right to know what 
                    personal information we collect, the right to delete personal information, and the right to 
                    opt-out of the sale of personal information (which we do not do). We do not discriminate 
                    against users who exercise their privacy rights.
                  </p>
                </section>
              </div>
            </ScrollArea>
          </div>
        </Card>
      </div>
    </div>
  );
}